import { Title } from '@solidjs/meta'
import { A, useParams, useSearchParams } from '@solidjs/router'
import { type Component, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import { catalogService, type PublicProductType } from '~/services/catalog.service'
import { encodeSpecFilter } from '~/services/product.service'

const money = (value: number) => `$${value.toLocaleString('es-CO')}`

const KIND_TABS = [
  { value: '', label: 'Todo' },
  { value: 'product', label: 'Productos' },
  { value: 'service', label: 'Servicios' }
]

/**
 * Catálogo público de una empresa. Es la puerta de entrada de la ficha de
 * producto y usa el mismo filtrado por característica del panel: las facetas
 * salen tanto de la ficha técnica como de los ejes de variante.
 */
const CatalogPublicPage: Component = () => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  /** `useParams` lo tipa como opcional; aquí siempre viene de la ruta. */
  const empresa = () => params.empresa ?? ''

  const [documentTitle, setDocumentTitle] = createSignal('Catálogo')

  const [kind, setKind] = createSignal('')
  const [search, setSearch] = createSignal('')
  const [specFilters, setSpecFilters] = createSignal<Record<string, string[]>>({})

  const categoryId = () => (searchParams.categoria ? Number(searchParams.categoria) : undefined)

  const [data] = createResource(
    () => ({
      empresa: empresa(),
      kind: kind(),
      q: search().trim(),
      specs: encodeSpecFilter(specFilters()),
      categoria: categoryId()
    }),
    async filters => {
      try {
        const result = await catalogService.list(filters.empresa, {
          limit: 60,
          kind: (filters.kind || undefined) as PublicProductType['kind'] | undefined,
          q: filters.q || undefined,
          specs: filters.specs || undefined,
          categoryId: filters.categoria
        })
        setDocumentTitle(`Catálogo | ${result.company.name}`)
        return result
      } catch {
        return null
      }
    }
  )

  const [facets] = createResource(
    () => ({ empresa: empresa(), kind: kind() }),
    async ({ empresa: slug, kind: k }) => {
      try {
        return await catalogService.facets(slug, k ? { kind: k as PublicProductType['kind'] } : undefined)
      } catch {
        return []
      }
    }
  )

  const toggleFacet = (key: string, value: string) => {
    setSpecFilters(current => {
      const values = current[key] ?? []
      const next = values.includes(value) ? values.filter(v => v !== value) : [...values, value]
      const { [key]: _drop, ...rest } = current
      return next.length > 0 ? { ...rest, [key]: next } : rest
    })
  }

  const activeCount = createMemo(() => Object.values(specFilters()).flat().length)

  return (
    <>
      <Title>{documentTitle()}</Title>

      <Container class="py-8">
        <div class="mb-8">
          <h1 class="text-3xl md:text-4xl font-bold text-foreground">{data()?.company.name ?? 'Catálogo'}</h1>
          <p class="text-foreground-muted mt-2">Todo lo que ofrecemos. Pide una cotización de lo que te interese.</p>
        </div>

        {/* Filtros */}
        <div class="flex flex-wrap items-center gap-3 mb-6">
          <div class="inline-flex rounded-xl border border-border overflow-hidden">
            <For each={KIND_TABS}>
              {tab => (
                <button
                  type="button"
                  onClick={() => setKind(tab.value)}
                  class={`px-4 py-2 text-sm font-medium transition-colors ${kind() === tab.value ? 'bg-primary text-white' : 'text-foreground-muted hover:bg-surface-muted'}`}
                >
                  {tab.label}
                </button>
              )}
            </For>
          </div>

          <div class="flex-1 min-w-56">
            <Input value={search()} onInput={e => setSearch(e.currentTarget.value)} placeholder="Buscar en el catálogo…" startIcon="material-symbols-light:search" />
          </div>

          <Show when={activeCount() > 0}>
            <Button variant="text" color="default" prependIcon="material-symbols-light:filter-alt-off-outline" onClick={() => setSpecFilters({})}>
              Limpiar filtros ({activeCount()})
            </Button>
          </Show>
        </div>

        {/* Características: cada valor es un chip que se enciende. */}
        <Show when={(facets() ?? []).length > 0}>
          <div class="space-y-3 mb-8">
            <For each={facets()}>
              {group => (
                <div class="flex flex-wrap items-center gap-2">
                  <span class="text-sm font-medium text-foreground-subtle w-28 shrink-0">{group.label}</span>
                  <For each={group.values}>
                    {value => (
                      <Chip
                        size="sm"
                        variant={(specFilters()[group.key] ?? []).includes(value.value) ? 'filled' : 'soft'}
                        color={(specFilters()[group.key] ?? []).includes(value.value) ? 'primary' : 'secondary'}
                        class="cursor-pointer"
                        onClick={() => toggleFacet(group.key, value.value)}
                      >
                        {value.label}
                      </Chip>
                    )}
                  </For>
                </div>
              )}
            </For>
          </div>
        </Show>

        <Show
          when={!data.loading}
          fallback={
            <div class="flex justify-center py-20">
              <Icon icon="material-symbols-light:progress-activity" size={40} class="animate-spin text-foreground-subtle" />
            </div>
          }
        >
          <Show
            when={(data()?.items.length ?? 0) > 0}
            fallback={
              <div class="text-center py-20">
                <Icon icon="material-symbols-light:search-off" size={48} class="text-foreground-subtle" />
                <p class="text-foreground-muted mt-4">No hay nada que coincida con lo que buscas.</p>
              </div>
            }
          >
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              <For each={data()!.items}>{item => <CatalogCard empresa={empresa()} item={item} />}</For>
            </div>

            <p class="text-sm text-foreground-subtle mt-8 text-center">
              {data()!.meta.total} {data()!.meta.total === 1 ? 'resultado' : 'resultados'}
            </p>
          </Show>
        </Show>
      </Container>
    </>
  )
}

const CatalogCard: Component<{ empresa: string; item: PublicProductType }> = props => (
  <A href={`/catalogo/${props.empresa}/${props.item.key}`} class="block group">
    <Card class="h-full overflow-hidden transition-all hover:shadow-xl hover:-translate-y-0.5">
      <div class="aspect-square bg-surface-muted flex items-center justify-center overflow-hidden">
        <Show
          when={props.item.images[0]}
          fallback={
            <Icon icon={props.item.kind === 'service' ? 'material-symbols-light:handyman-outline' : 'material-symbols-light:inventory-2-outline'} size={48} class="text-foreground-subtle/40" />
          }
        >
          <img src={props.item.images[0]} alt={props.item.name} class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </Show>
      </div>

      <div class="p-4">
        <Chip size="sm" variant="soft" color={props.item.kind === 'service' ? 'info' : 'secondary'}>
          {props.item.kind === 'service' ? 'Servicio' : 'Producto'}
        </Chip>
        <h2 class="font-semibold text-foreground mt-2 line-clamp-2 group-hover:text-primary transition-colors">{props.item.name}</h2>
        <Show when={props.item.description}>
          <p class="text-xs text-foreground-subtle mt-1 line-clamp-2">{props.item.description}</p>
        </Show>
        <p class="text-lg font-bold text-foreground mt-2">
          <Show when={props.item.kind === 'service'}>
            <span class="text-xs font-normal text-foreground-subtle">Desde </span>
          </Show>
          {money(props.item.price)}
        </p>
      </div>
    </Card>
  </A>
)

export default CatalogPublicPage
